import {
  ApiTokenListResponse,
  CancellationReasonDTO,
  MembershipForChangeDTO,
  SubscriptionMembershipChangeDatesDTO,
  SubscriptionMembershipChangeSchedulePostDTO,
  SubscriptionMembershipPreviousDTO,
  TokenPageableRequestParams,
} from "@gymflow/types";
import {
  infiniteQueryOptions,
  mutationOptions,
  QueryKey,
  queryOptions,
} from "@tanstack/react-query";

import { api } from "../../api";
import { apiV2 } from "../../apiV2";
import { queryClient } from "../../QueryClient";
import { tokenPagination } from "../../tokenPagination";

const keys = {
  all: () => ["memberApiSubscription"] as QueryKey,
  perUser: (userId: unknown) => [...keys.all(), userId] as QueryKey,
  cancellationReason: () => [...keys.all(), "cancellationReason"] as QueryKey,
  subscriptionChangeAvailableMemberships: (userId: unknown, params: unknown) =>
    [...keys.perUser(userId), "changeAvailable", params] as QueryKey,
  subscriptionChangeCalculateScheduleDates: (
    userId: unknown,
    params: unknown,
  ) => [...keys.perUser(userId), "changeDates", params] as QueryKey,
  getMembershipSubscriptionPrevious: (userId: unknown, params: unknown) =>
    [...keys.perUser(userId), "previous", params] as QueryKey,
};

export const memberApiSubscription = {
  keys,
  queries: {
    cancellationReason: () =>
      queryOptions({
        queryKey: keys.cancellationReason(),
        queryFn: async () => {
          return (
            await api.get<CancellationReasonDTO[]>(
              "/customer/cancellation-reason",
            )
          ).data;
        },
      }),
  },
  infiniteQueries: {
    getMembershipSubscriptionPrevious: (
      userId: string | undefined,
      params?: Partial<TokenPageableRequestParams>,
    ) =>
      infiniteQueryOptions({
        queryKey: keys.getMembershipSubscriptionPrevious(userId, params),
        queryFn: async ({ pageParam }) => {
          return (
            await api.get<
              ApiTokenListResponse<SubscriptionMembershipPreviousDTO>
            >("/customer/membership-subscription/previous", {
              params: { ...params, nextPageToken: pageParam, limit: 20 },
            })
          ).data;
        },
        ...tokenPagination,
      }),
    subscriptionChangeAvailableMemberships: (
      userId: string | undefined,
      params: {
        subscriptionId: number | undefined;
      } & Partial<TokenPageableRequestParams>,
    ) =>
      infiniteQueryOptions({
        queryKey: keys.subscriptionChangeAvailableMemberships(userId, params),
        queryFn: async ({ pageParam }) => {
          return (
            await api.get<ApiTokenListResponse<MembershipForChangeDTO>>(
              `/customer/subscription/${params.subscriptionId}/change/available-memberships`,
              {
                params: { ...params, nextPageToken: pageParam, limit: 20 },
              },
            )
          ).data;
        },
        enabled: !!params.subscriptionId,
        ...tokenPagination,
      }),
    subscriptionChangeCalculateScheduleDates: (
      userId: string | undefined,
      params: {
        subscriptionId: number | undefined;
      },
    ) =>
      infiniteQueryOptions({
        queryKey: keys.subscriptionChangeCalculateScheduleDates(userId, params),
        queryFn: async () => {
          return (
            await api.get<SubscriptionMembershipChangeDatesDTO>(
              `/customer/subscription/${params.subscriptionId}/change/calculate-schedule-dates`,
            )
          ).data;
        },
        enabled: !!params.subscriptionId,
        initialPageParam: undefined,
        getNextPageParam: () => undefined,
      }),
  },
  mutations: {
    subscriptionUnlinkSecondary: () =>
      mutationOptions({
        mutationFn: async ({
          subscriptionId,
        }: {
          subscriptionId: number;
          userId: string | undefined;
        }) => {
          return (
            await api.delete(
              `customer/subscription/${subscriptionId}/primary-link`,
            )
          ).data;
        },
        onSettled: async (_, __, { userId }) =>
          await queryClient.invalidateQueries({
            queryKey: apiV2.member.member.keys.id(userId),
          }),
      }),
    subscriptionRevokeCancellationByMembershipId: () =>
      mutationOptions({
        mutationFn: async ({
          subscriptionId,
        }: {
          subscriptionId: number;
          userId: string | undefined;
        }) => {
          return (
            await api.post(
              `customer/user/member/membership/revoke-cancellation/${subscriptionId}`,
            )
          ).data;
        },
        onSettled: async (_, __, { userId }) =>
          await queryClient.invalidateQueries({
            queryKey: apiV2.member.member.keys.id(userId),
          }),
      }),
    subscriptionChangeUnschedule: () =>
      mutationOptions({
        mutationFn: async ({
          subscriptionId,
        }: {
          subscriptionId: number;
          userId: string | undefined;
        }) => {
          return (
            await api.delete(
              `customer/subscription/${subscriptionId}/change/unschedule`,
            )
          ).data;
        },
        onSettled: async (_, __, { userId }) =>
          await queryClient.invalidateQueries({
            queryKey: apiV2.member.member.keys.id(userId),
          }),
      }),
    subscriptionChangeSchedule: () =>
      mutationOptions({
        mutationFn: async ({
          subscriptionId,
          params,
        }: {
          subscriptionId: number;
          userId: string;
          params: SubscriptionMembershipChangeSchedulePostDTO;
        }) => {
          return (
            await api.post(
              `/customer/subscription/${subscriptionId}/change/schedule`,
              params,
            )
          ).data;
        },
        onSettled: async (_, __, { userId }) =>
          await queryClient.invalidateQueries({
            queryKey: apiV2.member.member.keys.id(userId),
          }),
      }),
  },
};
